/**
 * NearestAssemblyFinder — "where can I worship near me?"
 *
 * Typing a town reorders the congregations already on the page (no geocoding,
 * no network). If any congregation carries coordinates, the visitor can also
 * let the browser sort by real distance.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Church, ChevronRight, MapPin, Search, Crosshair } from 'lucide-react';

const norm = (s) => (s || '').toString().trim().toLowerCase();

const haversineKm = (a, b) => {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLon = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(h)));
};

const CongregationCard = ({ c, distance }) => (
  <Link
    to={`/church/${c.slug}`}
    className="group border border-[color:var(--fwm-rule)] rounded-lg p-4 flex items-start gap-3 hover:border-[color:var(--fwm-accent)] transition-colors"
    data-testid={`grouping-congregation-${c.slug || c.id}`}
  >
    <Church className="h-4 w-4 mt-0.5 text-[color:var(--fwm-accent)] shrink-0" />
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-medium text-[color:var(--fwm-ink)] truncate">{c.name}</span>
      <span className="block text-xs text-[color:var(--fwm-muted)] mt-0.5 truncate">
        {[c.city, c.country].map((p) => (p || '').trim()).filter(Boolean).join(', ') || 'Location to be confirmed'}
        {distance != null ? ` · ${distance} km away` : ''}
      </span>
      {(c.service_times || []).length > 0 && (
        <span className="block mt-2 space-y-0.5" data-testid={`grouping-services-${c.slug || c.id}`}>
          {c.service_times.map((s, i) => (
            <span key={i} className="block text-xs text-[color:var(--fwm-body)]">
              {[s.weekday, s.start_time].filter(Boolean).join(' ')} · {s.name}
            </span>
          ))}
        </span>
      )}
    </span>
    <ChevronRight className="h-4 w-4 text-[color:var(--fwm-muted)] group-hover:text-[color:var(--fwm-accent)] shrink-0" />
  </Link>
);

export const NearestAssemblyFinder = ({ congregations, label }) => {
  const [query, setQuery] = React.useState('');
  const [origin, setOrigin] = React.useState(null);
  const [locating, setLocating] = React.useState(false);

  const canLocate = congregations.some((c) => c.latitude && c.longitude)
    && typeof navigator !== 'undefined' && navigator.geolocation;

  const withDistance = React.useMemo(() => congregations.map((c) => ({
    ...c,
    _distance: origin && c.latitude && c.longitude
      ? haversineKm(origin, { lat: c.latitude, lng: c.longitude })
      : null,
  })), [congregations, origin]);

  const q = norm(query);
  const townMatches = q
    ? withDistance.filter((c) => norm(c.city).includes(q)
        || norm(c.name).includes(q) || norm(c.address).includes(q))
    : [];
  const wideMatches = q && townMatches.length === 0
    ? withDistance.filter((c) => norm(c.region).includes(q) || norm(c.country).includes(q))
    : [];
  const matchedIds = new Set([...townMatches, ...wideMatches].map((c) => c.id));

  const bySortOrder = (list) => [...list].sort((a, b) => {
    if (a._distance != null && b._distance != null) return a._distance - b._distance;
    if (a._distance != null) return -1;
    if (b._distance != null) return 1;
    return a.name.localeCompare(b.name);
  });

  const rest = bySortOrder(withDistance.filter((c) => !matchedIds.has(c.id)));

  const locate = () => {
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setOrigin({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocating(false);
      },
      () => setLocating(false),
      { timeout: 8000 },
    );
  };

  return (
    <div data-testid="nearest-assembly-finder">
      <div className="flex flex-wrap items-center gap-2 mt-5">
        <div className="relative flex-1 min-w-0 sm:min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--fwm-muted)]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type your town or city"
            aria-label="Find the assembly nearest you"
            data-testid="nearest-assembly-input"
            className="w-full h-11 pl-9 pr-3 rounded-lg border border-[color:var(--fwm-rule)] bg-white/70 text-sm outline-none focus:border-[color:var(--fwm-accent)] transition-colors"
          />
        </div>
        {canLocate && (
          <button type="button" onClick={locate} disabled={locating}
                  data-testid="nearest-assembly-locate"
                  className="h-11 px-4 rounded-lg border border-[color:var(--fwm-rule)] text-sm inline-flex items-center gap-2 hover:border-[color:var(--fwm-accent)] transition-colors">
            <Crosshair className="h-4 w-4 text-[color:var(--fwm-accent)]" />
            {locating ? 'Locating…' : origin ? 'Sorted by distance' : 'Use my location'}
          </button>
        )}
      </div>

      {q && townMatches.length > 0 && (
        <div className="mt-6" data-testid="nearest-assembly-matches">
          <p className="text-xs uppercase tracking-[0.16em] text-[color:var(--fwm-muted)]">
            Closest to “{query.trim()}”
          </p>
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            {bySortOrder(townMatches).map((c) => (
              <CongregationCard key={c.id} c={c} distance={c._distance} />
            ))}
          </div>
        </div>
      )}

      {q && townMatches.length === 0 && (
        <div className="mt-6 rounded-lg border border-[color:var(--fwm-rule)] bg-white/60 p-4"
             data-testid="nearest-assembly-empty">
          <p className="text-sm text-[color:var(--fwm-ink)] flex items-center gap-2">
            <MapPin className="h-4 w-4 text-[color:var(--fwm-accent)]" />
            No assembly in “{query.trim()}” yet.
          </p>
          {wideMatches.length > 0 && (
            <>
              <p className="text-xs text-[color:var(--fwm-muted)] mt-2">
                These are the closest we have in that region:
              </p>
              <div className="grid sm:grid-cols-2 gap-3 mt-3" data-testid="nearest-assembly-wide">
                {bySortOrder(wideMatches).map((c) => (
                  <CongregationCard key={c.id} c={c} distance={c._distance} />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <div className="mt-6">
        {q && (
          <p className="text-xs uppercase tracking-[0.16em] text-[color:var(--fwm-muted)] mb-3"
             data-testid="nearest-assembly-rest-label">
            {townMatches.length > 0 || wideMatches.length > 0
              ? `Other congregations in this ${label}`
              : `Every congregation in this ${label}`}
          </p>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          {rest.map((c) => <CongregationCard key={c.id} c={c} distance={c._distance} />)}
        </div>
      </div>
    </div>
  );
};

export default NearestAssemblyFinder;
